import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import api from "../api";

// Lightweight analytics page for demand/supply with local ML utilities
// - Time series forecasting (linear regression)
// - Anomaly detection (z-score)
// - Clustering (simple k-means)
// This file avoids sending data externally; everything runs in-browser.

const primaryColor = "#005EB8";

// Helpers: month key Y-M format
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

// Aggregate rows by a date key into month counts
function aggregateByMonth(rows = [], dateKey) {
  const counts = {};
  rows.forEach((r) => {
    const v = r[dateKey];
    if (!v) return;
    const date = new Date(v);
    if (isNaN(date.getTime())) return;
    const key = monthKey(date);
    counts[key] = (counts[key] || 0) + 1;
  });
  const arr = Object.keys(counts)
    .sort()
    .map((m) => ({ month: m, value: counts[m] }));
  return arr;
}

// Linear regression (least squares) on y values; x is index
function linearRegressionPredict(history, predictMonths = 3) {
  if (!history || history.length < 2) return [];
  const y = history.map((d) => d.value);
  const n = y.length;
  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += y[i];
    sumXY += i * y[i];
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX || 1;
  const m = (n * sumXY - sumX * sumY) / denom;
  const b = (sumY - m * sumX) / n;

  const lastKey = history[n - 1].month;
  const [lastYear, lastMonth] = lastKey.split("-").map(Number);
  const preds = [];
  for (let i = 1; i <= predictMonths; i++) {
    let nm = lastMonth + i;
    let ny = lastYear;
    while (nm > 12) {
      nm -= 12;
      ny += 1;
    }
    const key = `${ny}-${String(nm).padStart(2, "0")}`;
    const x = n - 1 + i;
    const predicted = Math.max(0, Math.round(m * x + b));
    preds.push({ month: key, predicted });
  }
  return preds;
}

// z-score anomaly detection on history values
function detectAnomalies(history) {
  if (!history || history.length === 0) return [];
  const vals = history.map((d) => d.value);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const variance = vals.reduce((a, b) => a + (b - mean) * (b - mean), 0) / vals.length;
  const std = Math.sqrt(variance || 0);
  return history.map((d) => ({ ...d, z: std ? (d.value - mean) / std : 0, isAnomaly: Math.abs(std ? (d.value - mean) / std : 0) > 2 }));
}

// (clustering removed — keeping time-series and anomaly detection only)

export default function DemandSupplyAI() {
  const [loading, setLoading] = useState(false);
  const [demandRows, setDemandRows] = useState([]);
  const [supplyRows, setSupplyRows] = useState([]);
  const [aggDemand, setAggDemand] = useState([]);
  const [aggSupply, setAggSupply] = useState([]);
  const [predDemand, setPredDemand] = useState([]);
  const [predSupply, setPredSupply] = useState([]);
  const [anomaliesDemand, setAnomaliesDemand] = useState([]);
  const [anomaliesSupply, setAnomaliesSupply] = useState([]);

  useEffect(() => {
    // no-op: initial fetch happens via reload() below
  }, []);

  // reload function used by effect and refresh button
  const reload = async () => {
    setLoading(true);
    try {
      const resp = await api.get("/demandselect/report", { params: { parentCustomer: "null", buDesc: "null", pdlName: "null", offOn: "null" } });
      const demandData = resp.data || [];
      setDemandRows(demandData);
    } catch (err) {
      console.error("Error fetching demand:", err);
      setDemandRows([]);
    }

    try {
      const supplyResp = await api.post("/api/pool/supplyData", { source: "pdp" });
      const supplyData = supplyResp.data?.data ?? supplyResp.data ?? [];
      setSupplyRows(supplyData);
    } catch (err) {
      console.error("Error fetching supply:", err);
      setSupplyRows([]);
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => { reload(); }, []);

  // build monthly aggregation using a best-guess date key
  // helper: detect low anomalies with a reason
  function lowAnomalyReasons(history) {
    if (!history || history.length < 2) return [];
    const reasons = [];
    const vals = history.map((d) => d.value);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    for (let i = 1; i < history.length; i++) {
      const prev = history[i - 1].value;
      const cur = history[i].value;
      if (prev === 0) continue;
      const dropPct = (prev - cur) / prev;
      if (dropPct > 0.5) {
        reasons.push({ month: history[i].month, value: cur, reason: `Low (dropped ${(dropPct * 100).toFixed(0)}% vs previous month)` });
      } else if (cur < 0.5 * mean) {
        reasons.push({ month: history[i].month, value: cur, reason: `Low (below 50% of historical average ${Math.round(mean)})` });
      }
    }
    return reasons;
  }

  // build monthly aggregation for both datasets
  useEffect(() => {
    // demand
    if (!demandRows || demandRows.length === 0) {
      setAggDemand([]);
      setPredDemand([]);
      setAnomaliesDemand([]);
    } else {
      const dateKeys = ["So Submission Date", "So Submission_date", "So SubmissionDate", "So Submission", "Requirement Start Date", "Allocation Date", "Available From Date", "availableFromDate"];
      let usedKey = null;
      for (const k of dateKeys) if (demandRows.some((r) => r[k])) { usedKey = k; break; }
      if (!usedKey) {
        const sample = demandRows[0] || {};
        for (const k of Object.keys(sample)) {
          const v = sample[k];
          if (typeof v === "string" && new Date(v) !== "Invalid Date") { usedKey = k; break; }
        }
      }
      if (!usedKey) usedKey = Object.keys(demandRows[0])[0];
      const monthly = aggregateByMonth(demandRows, usedKey);
      setAggDemand(monthly);
      setPredDemand(linearRegressionPredict(monthly, 3));
      const annotated = detectAnomalies(monthly);
      setAnomaliesDemand(annotated.filter((d) => d.isAnomaly).map(d => ({...d, reason: null})).concat(lowAnomalyReasons(monthly)));
    }

    // supply
    if (!supplyRows || supplyRows.length === 0) {
      setAggSupply([]);
      setPredSupply([]);
      setAnomaliesSupply([]);
    } else {
      const dateKeys = ["Available From Date", "availableFromDate", "Allocation Date", "So Submission Date", "Requirement Start Date"];
      let usedKey = null;
      for (const k of dateKeys) if (supplyRows.some((r) => r[k])) { usedKey = k; break; }
      if (!usedKey) {
        const sample = supplyRows[0] || {};
        for (const k of Object.keys(sample)) {
          const v = sample[k];
          if (typeof v === "string" && new Date(v) !== "Invalid Date") { usedKey = k; break; }
        }
      }
      if (!usedKey) usedKey = Object.keys(supplyRows[0])[0];
      const monthly = aggregateByMonth(supplyRows, usedKey);
      setAggSupply(monthly);
      setPredSupply(linearRegressionPredict(monthly, 3));
      const annotated = detectAnomalies(monthly);
      setAnomaliesSupply(annotated.filter((d) => d.isAnomaly).map(d => ({...d, reason: null})).concat(lowAnomalyReasons(monthly)));
    }

    // clustering removed — no additional processing here
  }, [demandRows, supplyRows]);

  // build merged chart data aligning months from both series
  const chartData = useMemo(() => {
    const months = new Set();
    aggDemand.forEach((d) => months.add(d.month));
    aggSupply.forEach((d) => months.add(d.month));
    predDemand.forEach((d) => months.add(d.month));
    predSupply.forEach((d) => months.add(d.month));
    const sorted = Array.from(months).sort();
    return sorted.map((m) => ({
      month: m,
      demand: (aggDemand.find((d) => d.month === m) || {}).value ?? null,
      supply: (aggSupply.find((d) => d.month === m) || {}).value ?? null,
      demandPred: (predDemand.find((p) => p.month === m) || {}).predicted ?? null,
      supplyPred: (predSupply.find((p) => p.month === m) || {}).predicted ?? null,
    }));
  }, [aggDemand, aggSupply, predDemand, predSupply]);

  const summaryText = useMemo(() => {
    const totalDemand = demandRows.length;
    const totalSupply = supplyRows.length;
    const lastDemandMonth = aggDemand[aggDemand.length - 1];
    const lastSupplyMonth = aggSupply[aggSupply.length - 1];
    const lastDemandVal = lastDemandMonth ? lastDemandMonth.value : 0;
    const lastSupplyVal = lastSupplyMonth ? lastSupplyMonth.value : 0;
    const predNextDemand = predDemand[0] ? predDemand[0].predicted : 0;
    const predNextSupply = predSupply[0] ? predSupply[0].predicted : 0;
    return `Demand rows ${totalDemand}, Supply rows ${totalSupply}. Last demand month ${lastDemandMonth ? lastDemandMonth.month : 'n/a'}:${lastDemandVal}. Model predicts demand~${predNextDemand}, supply~${predNextSupply}.`;
  }, [demandRows, supplyRows, aggDemand, aggSupply, predDemand, predSupply]);

  // table and clustering removed — only chart and anomaly details remain

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h5" sx={{ color: primaryColor, fontWeight: "bold" }}>
              Demand & Supply AI Insights (WIP)
            </Typography>
          </Grid>
          <Grid item xs />
          <Grid item>
            <Typography variant="caption">Showing both Demand & Supply for comparison</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="subtitle2">Month-wise</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="demand" stroke={primaryColor} name="Demand Actual" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="supply" stroke="#2E7D32" name="Supply Actual" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="demandPred" stroke="#FF6B6B" strokeDasharray="5 5" name="Demand Pred" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="supplyPred" stroke="#FFA726" strokeDasharray="5 5" name="Supply Pred" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 360, overflow: 'auto' }}>
            <Typography variant="subtitle2">Anomaly Details</Typography>
            {anomaliesDemand.length === 0 && anomaliesSupply.length === 0 ? (
              <Typography sx={{ mt: 1 }}>No significant anomalies detected.</Typography>
            ) : (
              <Box>
                {anomaliesDemand.map((a) => (
                  <Box key={`d-${a.month}`} sx={{ mt: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{a.month} (Demand)</strong>: {a.value} {a.z !== undefined ? `(z=${a.z.toFixed(2)})` : ''} {a.reason ? `- ${a.reason}` : ''}</Typography>
                  </Box>
                ))}
                {anomaliesSupply.map((a) => (
                  <Box key={`s-${a.month}`} sx={{ mt: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{a.month} (Supply)</strong>: {a.value} {a.z !== undefined ? `(z=${a.z.toFixed(2)})` : ''} {a.reason ? `- ${a.reason}` : ''}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        {/* clustering and tabular data removed */}
      </Grid>
    </Box>
  );
}
